if (!customElements.get('cart-discount-component')) {
  customElements.define(
    'cart-discount-component',
    class CartDiscount extends HTMLElement {
      constructor() {
        super();
        this.activeFetch = null;
        
        // Setup event listeners
        const form = this.querySelector('.cart-discount__form');
        if (form) {
          form.addEventListener('submit', this.applyDiscount.bind(this));
        }

        const removeButtons = this.querySelectorAll('[data-discount-code]');
        removeButtons.forEach((pill) => {
          const button = pill.querySelector('.cart-discount__pill-remove');
          if (button) {
            button.addEventListener('click', this.removeDiscount.bind(this));
          }
        });
      }

      createAbortController() {
        if (this.activeFetch) {
          this.activeFetch.abort();
        }

        const abortController = new AbortController();
        this.activeFetch = abortController;
        return abortController;
      }

      async applyDiscount(event) {
        event.preventDefault();
        event.stopPropagation();

        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const discountInput = form.querySelector('input[name="discount"]');
        const sectionId = this.dataset.sectionId;
        
        if (!discountInput || !sectionId) return;

        const discountCodeValue = discountInput.value.trim();
        if (!discountCodeValue) return;

        const cartDiscountError = this.querySelector('.cart-discount__error');
        const cartDiscountErrorDiscountCode = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
        const cartDiscountErrorShipping = this.querySelector('[ref="cartDiscountErrorShipping"]');

        const abortController = this.createAbortController();

        try {
          const existingDiscounts = this.getExistingDiscounts();
          if (existingDiscounts.includes(discountCodeValue)) return;

          // Hide errors
          cartDiscountError?.classList.add('hidden');
          cartDiscountErrorDiscountCode?.classList.add('hidden');
          cartDiscountErrorShipping?.classList.add('hidden');

          const body = JSON.stringify({
            discount: [...existingDiscounts, discountCodeValue].join(','),
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          });

          const response = await fetch(`${window.routes.cart_update_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: body,
            signal: abortController.signal,
          });

          const data = await response.json();

          // Check if discount is not applicable
          const invalidDiscount = data.discount_codes?.find((discount) => {
            return discount.code === discountCodeValue && discount.applicable === false;
          });

          if (invalidDiscount) {
            discountInput.value = '';
            this.handleDiscountError('discount_code', cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping);
            return;
          }

          const newHtml = data.sections[sectionId];
          if (newHtml) {
            const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
            const section = parsedHtml.getElementById(`shopify-section-${sectionId}`);
            const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
            
            if (section) {
              const codes = Array.from(discountCodes)
                .map((element) => element instanceof HTMLLIElement ? element.dataset.discountCode : null)
                .filter(Boolean);
              
              // Check for shipping discount error
              const validDiscount = data.discount_codes?.find((discount) => {
                return discount.code === discountCodeValue && discount.applicable === true;
              });

              if (
                codes.length === existingDiscounts.length &&
                codes.every((code) => existingDiscounts.includes(code)) &&
                validDiscount
              ) {
                this.handleDiscountError('shipping', cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping);
                discountInput.value = '';
                return;
              }
            }

            // Update cart drawer using renderContents
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(data);
            }
            
            // Dispatch event for other cart updates
            document.dispatchEvent(new CustomEvent('cart:discount-updated', {
              detail: { cart: data }
            }));
          }

          discountInput.value = '';
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Error applying discount:', error);
          }
        } finally {
          this.activeFetch = null;
        }
      }

      async removeDiscount(event) {
        event.preventDefault();
        event.stopPropagation();

        const button = event.currentTarget;
        const pill = button.closest('.cart-discount__pill');
        const sectionId = this.dataset.sectionId;
        
        if (!pill || !sectionId) return;

        const discountCode = pill.dataset.discountCode;
        if (!discountCode) return;

        const existingDiscounts = this.getExistingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        const abortController = this.createAbortController();

        try {
          const body = JSON.stringify({
            discount: existingDiscounts.join(','),
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          });

          const response = await fetch(`${window.routes.cart_update_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: body,
            signal: abortController.signal,
          });

          const data = await response.json();

          // Update cart drawer using renderContents
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
          }
          
          // Dispatch event for other cart updates
          document.dispatchEvent(new CustomEvent('cart:discount-updated', {
            detail: { cart: data }
          }));
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Error removing discount:', error);
          }
        } finally {
          this.activeFetch = null;
        }
      }

      handleDiscountError(type, cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping) {
        const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
        cartDiscountError?.classList.remove('hidden');
        target?.classList.remove('hidden');
      }

      getExistingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll('.cart-discount__pill');
        
        for (const pill of discountPills) {
          if (pill instanceof HTMLLIElement && pill.dataset.discountCode) {
            discountCodes.push(pill.dataset.discountCode);
          }
        }

        return discountCodes;
      }
    }
  );
}
